// src/utils/brandProtection.js
// Event-driven entitlement check. We fetch on first hook mount, on tab
// visibility change (catches users returning from external checkout), and on
// explicit `refreshEntitlements()` calls (e.g. after closing the in-app
// CheckoutModal). No background polling.
//
// Module-level state:
//   - `cached`     last successful response, in-memory only (no sessionStorage)
//   - `inflight`   dedupes concurrent fetches across consumers
//   - `subscribers`set of state setters from mounted hook instances; any
//                  successful fetch fans out updates to all of them.

import { useEffect, useState } from "react";
import { getToken, redirectToLogin } from "./auth";

const CHECK_URL =
  "https://whiteintel.io/api/regular/app/v2/watchlist/brand_protection_check.php";

let cached = null; // { brandAvailable, investigationsAvailable, webhookAvailable, licenseType, licenseSource } | null
let inflight = null;
const subscribers = new Set(); // (entitlements|null) => void

let visibilityListenerAttached = false;
function ensureVisibilityListener() {
  if (visibilityListenerAttached) return;
  if (typeof document === "undefined") return;
  document.addEventListener("visibilitychange", () => {
    // Only refetch when becoming visible AND we already had a value to begin
    // with. The first mount is what populates `cached`; visibility transitions
    // before that are no-ops.
    if (document.visibilityState === "visible" && cached) {
      fetchEntitlements({ force: true });
    }
  });
  visibilityListenerAttached = true;
}

// Tri-state coercion: only `true`/`false` is meaningful. Anything else
// (missing field, string "true", number 1) collapses to `null` so we don't
// accidentally lock a feature when the backend response shape is unexpected.
function asBool(v) {
  return typeof v === "boolean" ? v : null;
}

function notify() {
  subscribers.forEach((cb) => {
    try {
      cb(cached);
    } catch {
      /* keep other subscribers alive on a single bad callback */
    }
  });
}

export async function fetchEntitlements({ force = false } = {}) {
  if (!force && cached) return cached;
  if (inflight) return inflight;

  inflight = (async () => {
    try {
      const res = await fetch(CHECK_URL, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${getToken()}`,
        },
        cache: "no-store",
        body: JSON.stringify({}),
      });
      if ([401, 419, 440].includes(res.status)) {
        redirectToLogin();
        return null;
      }
      const data = await res.json().catch(() => null);
      if (data?.success) {
        cached = {
          brandAvailable: asBool(data.brand_protection_available),
          investigationsAvailable: asBool(data.investigations_available),
          webhookAvailable: asBool(data.webhook_available),
          licenseType: data.license_type || null,
          licenseSource: data.license_source || null,
        };
        notify();
      }
    } catch {
      /* best-effort: leave cache as-is */
    }
    return cached;
  })();

  try {
    return await inflight;
  } finally {
    inflight = null;
  }
}

// Backward-compat alias — older callers still import this name.
export const fetchBrandProtectionAccess = fetchEntitlements;

/**
 * Force a re-check of the entitlements. Call after any flow that might have
 * changed the user's license (e.g. closing the CheckoutModal on Upgrades).
 */
export function refreshEntitlements() {
  return fetchEntitlements({ force: true });
}

export function clearBrandProtectionCache() {
  cached = null;
  notify();
}

function useFeatureAccess(pickAvailable) {
  const [state, setState] = useState(() => ({
    loading: cached === null,
    available: cached ? pickAvailable(cached) : null,
    licenseType: cached?.licenseType ?? null,
    licenseSource: cached?.licenseSource ?? null,
  }));

  useEffect(() => {
    ensureVisibilityListener();
    let active = true;

    const handleResult = (result) => {
      if (!active) return;
      setState({
        loading: false,
        available: result ? pickAvailable(result) : null,
        licenseType: result?.licenseType ?? null,
        licenseSource: result?.licenseSource ?? null,
      });
    };

    subscribers.add(handleResult);

    // Trigger initial fetch (single-flight across all consumers).
    fetchEntitlements().then(handleResult);

    return () => {
      active = false;
      subscribers.delete(handleResult);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return state;
}

export function useBrandProtectionAccess() {
  return useFeatureAccess((c) => c.brandAvailable);
}

export function useInvestigationsAccess() {
  return useFeatureAccess((c) => c.investigationsAvailable);
}

export function useWebhookAccess() {
  return useFeatureAccess((c) => c.webhookAvailable);
}
